import { useCallback } from 'react';
import { useTranslation } from '@wix/cli-i18n';
import type { DevCenterComponent } from '../dev-center-component.js';
import { composeValidations, useValidation } from './common.js';
import { useExtensions } from '../../../components/extensions/useExtensions.js';

const PotentialCustomElementNameRegEx =
  //eslint-disable-next-line no-misleading-character-class
  /^[a-z](?:[.0-9_a-z\xB7\xC0-\xD6\xD8-\xF6\xF8-\u037D\u037F-\u1FFF\u200C\u200D\u203F\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]|[\uD800-\uDB7F][\uDC00-\uDFFF])*-(?:[\x2D.0-9_a-z\xB7\xC0-\xD6\xD8-\xF6\xF8-\u037D\u037F-\u1FFF\u200C\u200D\u203F\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]|[\uD800-\uDB7F][\uDC00-\uDFFF])*$/;

const isPotentialCustomElementName = function (string: string) {
  return PotentialCustomElementNameRegEx.test(string);
};

const reservedNames = new Set([
  'annotation-xml',
  'color-profile',
  'font-face',
  'font-face-src',
  'font-face-uri',
  'font-face-format',
  'font-face-name',
  'missing-glyph',
]);

export const validateCustomElementName = (
  extensionType: 'Site widget' | 'Site plugin'
) =>
  composeValidations<string>([
    [(name) => Boolean(name), `Missing ${extensionType} name.`],
    [
      (name) => !/[A-Z]/.test(name),
      `${extensionType} names must not contain uppercase ASCII characters.`,
    ],
    [
      (name) => name.includes('-'),
      `${extensionType} names must contain a hyphen. Example: unicorn-cake`,
    ],
    [
      (name) => !/^\d/i.test(name),
      `${extensionType} names must not start with a digit.`,
    ],
    [
      (name) => !/^-/i.test(name),
      `${extensionType} names must not start with a hyphen.`,
    ],
    [(name) => isPotentialCustomElementName(name), 'Invalid element name.'],
    [
      (name) => !reservedNames.has(name),
      "The supplied element name is reserved and can't be used.\nSee: https://html.spec.whatwg.org/multipage/scripting.html#valid-custom-element-name",
    ],
  ]);

function customElementExistsValidation(
  existingCustomElements: Array<
    DevCenterComponent<'CustomElement' | 'SitePlugin'>
  >
) {
  return (folder: string) =>
    existingCustomElements.every(
      (existingCustomElement) => folder !== existingCustomElement.tagName
    );
}

export const useCustomElementDirectoryValidation = (
  extensionType: 'Site widget' | 'Site plugin'
) => {
  const { t } = useTranslation();
  const { getExtensions } = useExtensions();

  const customElements = getExtensions('CustomElement');
  const sitePlugins = getExtensions('SitePlugin');

  const validation = useValidation(
    composeValidations([
      [
        customElementExistsValidation([...customElements, ...sitePlugins]),
        t('generate_command.custom_element.validation.element_exists', {
          extensionType: extensionType.toLowerCase(),
        }),
      ],
      [
        (extensionName: string) =>
          validateCustomElementName(extensionType)(extensionName).valid,
        t('generate_command.custom_element.validation.element_invalid', {
          extensionType: extensionType.toLowerCase(),
        }),
      ],
    ])
  );

  return useCallback(validation, [validation]);
};

export const useCustomElementNameValidation = () => {
  const { t } = useTranslation();

  const validation = useValidation(
    composeValidations([
      [
        (name: string) => name.length <= 30,
        t('generate_command.custom_element.choose_name.error'),
      ],
    ])
  );

  return useCallback(validation, [validation]);
};
